import * as React from 'react';

import {
  Button,
  ConfirmPopoverButton,
  DataGridColumn,
  GroupRow,
} from '@rematter/ui-web';
import startCase from 'lodash/startCase';
import {
  LucideArrowDown,
  LucideArrowUp,
  LucidePlus,
  LucideTrash2,
} from 'lucide-react';

import { EvaluatedTierData } from '../../../_graphql/queryEvaluateFormulas';
import { useTranslations } from '../../../i18n/useTranslations';
import {
  ItemPricingRecord,
  ItemPricingTierRecord,
  TierInput,
  TierRow,
  VariableRecord,
} from '../../../types/pricing';
import { PriceField } from '../PriceField';

const priceFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
  useGrouping: false,
});

const formatPriceFormula = (formula: string | null | undefined): string => {
  if (!formula) return '—';
  const trimmed = formula.trim();
  if (!trimmed) return '—';
  const num = Number(trimmed);
  return !isNaN(num) ? priceFormatter.format(num) : formula;
};

export type TierEditValues = {
  maxAmount: string;
  targetPrice: string;
  minPrice: string;
  maxPrice: string;
  scrapValue: string;
  minPriceVariableIds: string[];
  targetPriceVariableIds: string[];
  maxPriceVariableIds: string[];
  itemId?: string;
  itemName?: string;
  pricingUnit?: string;
};

export const flattenToTierRows = (pricings: ItemPricingRecord[]): TierRow[] =>
  pricings.flatMap((pp) =>
    pp.tiers.map((tier, index) => ({
      id: tier.id,
      itemPricingId: pp.id,
      itemId: pp.item.id,
      itemName: pp.item.name,
      itemGroupName: pp.item.itemGroup?.name ?? null,
      pricingUnit: pp.pricingUnit,
      itemPricingPosition: pp.position,
      tier,
      tierCount: pp.tiers.length,
      tierIndex: index,
    })),
  );

export const tierRecordToInput = (
  tier: ItemPricingTierRecord,
  position: number,
): TierInput => ({
  maxAmount: tier.maxAmount,
  minPrice: {
    formula: tier.minPrice.formula,
    variableIds: tier.minPrice.variableIds,
  },
  targetPrice: {
    formula: tier.targetPrice.formula,
    variableIds: tier.targetPrice.variableIds,
  },
  maxPrice: {
    formula: tier.maxPrice.formula,
    variableIds: tier.maxPrice.variableIds,
  },
  scrapValue: tier.scrapValue,
  position,
});

export const tierEditValuesFromTierRow = (row: TierRow): TierEditValues => {
  const tier = row.tier;
  return {
    maxAmount: tier.maxAmount != null ? String(tier.maxAmount) : '',
    targetPrice: tier.targetPrice.formula ?? '',
    minPrice: tier.minPrice.formula ?? '',
    maxPrice: tier.maxPrice.formula ?? '',
    scrapValue: String(tier.scrapValue),
    minPriceVariableIds: tier.minPrice.variableIds ?? [],
    targetPriceVariableIds: tier.targetPrice.variableIds ?? [],
    maxPriceVariableIds: tier.maxPrice.variableIds ?? [],
  };
};

export const buildTierInputFromEdit = (
  values: TierEditValues,
  pricingMode?: string,
): TierInput => {
  const isCRV = pricingMode === 'CRV';
  return {
    maxAmount: values.maxAmount ? Number(values.maxAmount) : null,
    minPrice: {
      formula: isCRV ? '' : values.minPrice.trim(),
      variableIds: values.minPriceVariableIds ?? [],
    },
    targetPrice: {
      formula: isCRV ? '' : values.targetPrice.trim(),
      variableIds: values.targetPriceVariableIds ?? [],
    },
    maxPrice: {
      formula: isCRV ? '' : values.maxPrice.trim(),
      variableIds: values.maxPriceVariableIds ?? [],
    },
    scrapValue: Number(values.scrapValue) || 0,
    position: 0,
  };
};

type SelectedCell = {
  tierId: string;
  field: 'targetPrice' | 'minPrice' | 'maxPrice';
};

type Props = {
  pendingChanges: Map<string, TierEditValues>;
  onCellChange: (
    tierId: string,
    itemPricingId: string,
    field: string,
    value: string,
  ) => void;
  onDelete?: (itemPricingId: string) => void;
  onAddTier?: (itemPricingId: string) => void;
  onReorder?: (itemPricingId: string, direction: 'up' | 'down') => void;
  totalItemPricings?: number;
  isReadOnly?: boolean;
  pricingMode?: 'Standard' | 'CRV';
  variables?: VariableRecord[];
  evaluatedPrices?: Record<string, EvaluatedTierData>;
  onSelectCell?: (cell: SelectedCell | null) => void;
};

export const useProductPricingColumns = ({
  pendingChanges,
  onCellChange,
  onDelete,
  onAddTier,
  onReorder,
  totalItemPricings,
  isReadOnly,
  pricingMode,
  variables,
  evaluatedPrices,
  onSelectCell,
}: Props) => {
  const t = useTranslations();
  const vars = variables ?? [];

  const columns: DataGridColumn<TierRow>[] = React.useMemo(() => {
    const inputClass =
      'w-full rounded border border-neutral/30 bg-transparent px-2 py-1 text-sm focus:border-primary focus:outline-none';

    const renderEvaluated = (val: number | null | undefined) => {
      if (val == null) return null;
      return (
        <div className="text-xs text-neutral/50">
          = {priceFormatter.format(val)}
        </div>
      );
    };

    const cols: DataGridColumn<TierRow>[] = [
      {
        key: 'product',
        header: t.pricing.itemPricings.table.columns.product,
        width: 180,
        cell: () => null,
        groupCell: (group: GroupRow<TierRow>) => (
          <div className="flex min-h-8 items-center text-left font-medium">
            {group.children[0]?.itemName}
          </div>
        ),
      },
      {
        key: 'pricingUnit',
        header: t.pricing.itemPricings.table.columns.pricingUnit,
        width: 80,
        cell: () => null,
        groupCell: (group: GroupRow<TierRow>) => (
          <div className="text-left">
            {startCase(group.children[0]?.pricingUnit)}
          </div>
        ),
      },
      {
        key: 'maxAmount',
        header: t.pricing.itemPricings.table.columns.maxAmount,
        width: 90,
        cell: ({ row }) => {
          if (isReadOnly) {
            const ma = row.tier.maxAmount;
            return (
              <div className="text-left text-neutral/60">
                {ma != null ? ma : '—'}
              </div>
            );
          }
          const pending = pendingChanges.get(row.id);
          const value =
            pending?.maxAmount ??
            (row.tier.maxAmount != null ? String(row.tier.maxAmount) : '');
          return (
            <input
              type="text"
              className={inputClass}
              value={value}
              placeholder="—"
              onChange={(e) =>
                onCellChange(
                  row.id,
                  row.itemPricingId,
                  'maxAmount',
                  e.target.value,
                )
              }
            />
          );
        },
      },
      {
        key: 'targetPrice',
        header: t.pricing.itemPricings.table.columns.targetPrice,
        width: 150,
        cell: ({ row }) => {
          if (isReadOnly) {
            const evaluated = evaluatedPrices?.[row.id];
            return (
              <div className="text-left">
                <div>{formatPriceFormula(row.tier.targetPrice.formula)}</div>
                {renderEvaluated(evaluated?.targetPrice)}
              </div>
            );
          }
          const pending = pendingChanges.get(row.id);
          const value =
            pending?.targetPrice ?? row.tier.targetPrice.formula ?? '';
          const evaluated = evaluatedPrices?.[row.id];
          return (
            <div
              onFocus={() =>
                onSelectCell?.({ tierId: row.id, field: 'targetPrice' })
              }
            >
              <PriceField
                value={value}
                onChange={(v) =>
                  onCellChange(row.id, row.itemPricingId, 'targetPrice', v)
                }
                variables={vars}
              />
              {!pending && renderEvaluated(evaluated?.targetPrice)}
            </div>
          );
        },
      },
      {
        key: 'minPrice',
        header: t.pricing.itemPricings.table.columns.minPrice,
        width: 150,
        cell: ({ row }) => {
          if (isReadOnly) {
            const evaluated = evaluatedPrices?.[row.id];
            return (
              <div className="text-left">
                <div>{formatPriceFormula(row.tier.minPrice.formula)}</div>
                {renderEvaluated(evaluated?.minPrice)}
              </div>
            );
          }
          const pending = pendingChanges.get(row.id);
          const value = pending?.minPrice ?? row.tier.minPrice.formula ?? '';
          const evaluated = evaluatedPrices?.[row.id];
          return (
            <div
              onFocus={() =>
                onSelectCell?.({ tierId: row.id, field: 'minPrice' })
              }
            >
              <PriceField
                value={value}
                onChange={(v) =>
                  onCellChange(row.id, row.itemPricingId, 'minPrice', v)
                }
                variables={vars}
              />
              {!pending && renderEvaluated(evaluated?.minPrice)}
            </div>
          );
        },
      },
      {
        key: 'maxPrice',
        header: t.pricing.itemPricings.table.columns.maxPrice,
        width: 150,
        cell: ({ row }) => {
          if (isReadOnly) {
            const evaluated = evaluatedPrices?.[row.id];
            return (
              <div className="text-left">
                <div>{formatPriceFormula(row.tier.maxPrice.formula)}</div>
                {renderEvaluated(evaluated?.maxPrice)}
              </div>
            );
          }
          const pending = pendingChanges.get(row.id);
          const value = pending?.maxPrice ?? row.tier.maxPrice.formula ?? '';
          const evaluated = evaluatedPrices?.[row.id];
          return (
            <div
              onFocus={() =>
                onSelectCell?.({ tierId: row.id, field: 'maxPrice' })
              }
            >
              <PriceField
                value={value}
                onChange={(v) =>
                  onCellChange(row.id, row.itemPricingId, 'maxPrice', v)
                }
                variables={vars}
              />
              {!pending && renderEvaluated(evaluated?.maxPrice)}
            </div>
          );
        },
      },
      {
        key: 'scrapValue',
        header: t.pricing.itemPricings.table.columns.scrapValue,
        width: 90,
        cell: ({ row }) => {
          if (isReadOnly) {
            return (
              <div className="text-left">
                {priceFormatter.format(row.tier.scrapValue)}
              </div>
            );
          }
          const pending = pendingChanges.get(row.id);
          const value = pending?.scrapValue ?? String(row.tier.scrapValue);
          return (
            <input
              type="number"
              step="0.01"
              className={inputClass}
              value={value}
              onChange={(e) =>
                onCellChange(
                  row.id,
                  row.itemPricingId,
                  'scrapValue',
                  e.target.value,
                )
              }
            />
          );
        },
      },
    ];

    if (!isReadOnly) {
      cols.push({
        key: 'actions',
        header: '',
        width: onReorder ? 170 : 100,
        cell: () => null,
        groupCell: (group: GroupRow<TierRow>) => {
          const ppId = String(group.groupKey);
          const position = group.children[0]?.itemPricingPosition ?? 0;
          const total = totalItemPricings ?? 0;
          return (
            <div className="flex items-center justify-end gap-1">
              {onReorder && (
                <>
                  <Button
                    variant="ghost"
                    color="neutral"
                    size="small"
                    icon={<LucideArrowUp size={14} />}
                    onPress={() => onReorder(ppId, 'up')}
                    isDisabled={position === 0}
                  />
                  <Button
                    variant="ghost"
                    color="neutral"
                    size="small"
                    icon={<LucideArrowDown size={14} />}
                    onPress={() => onReorder(ppId, 'down')}
                    isDisabled={position >= total - 1}
                  />
                </>
              )}
              <Button
                variant="ghost"
                color="primary"
                size="small"
                icon={<LucidePlus size={14} />}
                onPress={() => onAddTier?.(ppId)}
              />
              <ConfirmPopoverButton
                onConfirm={() => onDelete?.(ppId)}
                message={t.pricing.itemPricings.deleteConfirm}
                confirmLabel={t.common.actions.delete}
                cancelLabel={t.common.actions.cancel}
                buttonProps={{
                  variant: 'ghost',
                  size: 'small',
                  icon: <LucideTrash2 size={14} />,
                }}
              />
            </div>
          );
        },
      });
    }

    return cols;
  }, [
    t,
    pendingChanges,
    onCellChange,
    onDelete,
    onAddTier,
    onReorder,
    totalItemPricings,
    isReadOnly,
    vars,
    evaluatedPrices,
    onSelectCell,
  ]);

  const isCRV = pricingMode === 'CRV';

  const columnVisibilityState = React.useMemo(
    () =>
      Object.fromEntries(
        columns.map((col) => {
          if (
            isCRV &&
            (col.key === 'targetPrice' ||
              col.key === 'minPrice' ||
              col.key === 'maxPrice')
          ) {
            return [col.key, false];
          }
          return [col.key, true];
        }),
      ),
    [columns, isCRV],
  );

  return { columns, columnVisibilityState };
};

export type { SelectedCell };
